import CryptoJS from 'crypto-js'

import { ClientStatus, WebStatus, FastLoginOptions, DecisionResult, LoginStrategy } from './types'
import { Environment, Account } from '../../types'

// Imports needed for internal logic
import { getApiConfig, ApiConfig, getSiteConfig } from '../../config'
import { localhostGenSignatureKeyApi, localhostCheckLoginStateApi, localhostGetAuthorizeCodeApi } from './api/localhost'
import { generateRd, isValidOrigin, isMobile } from '../../utils'
import { fetchWebStatus, authorizeLoginApi } from '../../api/user'
import { fetchMobileAuthCode, exchangeMobileToken } from './api/mobile'

// Imports needed for re-export
import { decidePCLoginStrategy } from './strategies/pc'
import { decideMobileLoginStrategy } from './strategies/mobile'
import { parseClientParam } from './parser'

// Re-export specific internal functions for advanced usage or testing/demo
export { decidePCLoginStrategy, decideMobileLoginStrategy, parseClientParam }

import { getGlobalEnv, isDebug } from '../../global'

export interface FastLoginConfig {
  // 继承全局配置，但可局部覆盖
  env?: Environment
  debug?: boolean

  ports?: number[] // 客户端可能占用的端口范围
  autoHandleStrategy?: boolean // 是否自动执行策略处理 (UI 渲染等)，默认为 true
  onlyCheckWithClParam?: boolean // 是否仅在检测到 _cl 参数时才执行决策检测 (默认 false)
  onSuccess?: (data: any) => void // 登录成功回调
  onError?: (error: Error) => void // 登录失败回调
  userInfo?:
    | {
        account: string
        avatar: string
        userid: number
        [key: string]: any
      }
    | undefined
}

interface PortProbeContext {
  port: number
  timestamp: number
  signaturePrefix: string
  rd: string
  signatureKey: string | null
  clientName: string | null
}

// 模块内部状态
let moduleConfig: FastLoginConfig = {
  ports: [63701, 63702, 63703, 63704, 63705, 63706, 63707, 63708, 63709, 63710],
  autoHandleStrategy: true,
  onlyCheckWithClParam: false,
}

let initialized = false
let lastDecision: DecisionResult | null = null

// 提交状态
let isSubmitting = false
let pollTimer: any = null
let pollCountdown = 450 // 轮询次数限制
let sseSource: EventSource | null = null // SSE 连接实例

// 临时存储探测数据
const portProbeMap: Record<number, PortProbeContext> = {}

const MOBILE_APP_SCHEME_MAP: Record<string, string> = {
  sun: 'sunlogin',
  pgy: 'pgyvisitor',
}

/**
 * 重置提交状态和轮询
 */
function resetSubmission() {
  isSubmitting = false
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
  if (sseSource) {
    sseSource.close()
    sseSource = null
  }
}

/**
 * 销毁 SDK 实例/清理资源
 */
export function destroy() {
  if (isDebug()) {
    console.log('[OrayFastLoginSDK] Destroying/Cleaning up resources...')
  }
  resetSubmission()
}

/**
 * 执行 PC 端快捷登录
 * oray.fastLogin.handlePC
 */
export async function handlePC(account: Account) {
  if (!isValidOrigin(window.location.origin)) {
    return null
  }
  if (isSubmitting) return

  // 如果配置了仅在有 _cl 参数时才允许，则再次校验
  if (moduleConfig.onlyCheckWithClParam) {
    const clParam = parseClientParam(window.location.search)
    if (!clParam) {
      const err = new Error('Current configuration requires _cl param for fast login')
      moduleConfig.onError?.(err)
      return
    }
  }

  // 使用合并后的环境配置
  const env = moduleConfig.env || getGlobalEnv()
  const apiConfig = getApiConfig(env)

  const portContext = Object.values(portProbeMap).find((p) => p.clientName === account.source)

  if (!portContext || !portContext.signatureKey) {
    const err = new Error('无法连接到客户端，授权登录失败')
    moduleConfig.onError?.(err)
    throw err
  }

  try {
    isSubmitting = true

    // 1. 发起授权请求
    const res = await localhostGetAuthorizeCodeApi({
      baseUrl: apiConfig.localhostBase,
      userid: account.userid,
      port: portContext.port,
      timestamp: portContext.timestamp,
      signatureKey: portContext.signatureKey,
      rd: portContext.rd,
    })

    if (!res) {
      throw new Error('客户端授权请求失败')
    }

    // 2. 开始轮询
    pollCountdown = 450 // 重置计数
    startPolling(portContext, account)
  } catch (error: any) {
    resetSubmission()
    const err = new Error(error.message || '授权登录失败')
    moduleConfig.onError?.(err)
    throw err
  }
}

/**
 * 执行移动端快捷登录
 * oray.fastLogin.handleMobile
 */
export async function handleMobile(options: { appScheme?: string; brand: string; downloadUrl?: string }) {
  if (isSubmitting) return

  const appScheme = options.appScheme || MOBILE_APP_SCHEME_MAP[options.brand]
  const downloadUrl = options.downloadUrl // downloadUrl 暂未放入全局 config，这里只取参数

  if (!options.brand || !appScheme) {
    const err = new Error('移动端快捷登录配置缺失: brand 或无法映射到 appScheme')
    moduleConfig.onError?.(err)
    console.error('[OrayFastLoginSDK]', err)
    return
  }

  const env = moduleConfig.env || getGlobalEnv()
  const apiConfig = getApiConfig(env)
  const siteConfig = getSiteConfig(env)

  try {
    isSubmitting = true

    // 1. 获取授权 Code
    const res = await fetchMobileAuthCode(apiConfig)
    if (!res || res.status !== 200 || !res.data?.code) {
      throw new Error('获取授权 Code 失败')
    }
    const authCode = res.data.code

    // 2. 建立 SSE 连接监听授权状态
    const sseUrl = `${apiConfig.authApiBase}/fast-login/sse?code=${authCode}`

    if (isDebug()) console.log('[OrayFastLoginSDK] Establishing SSE:', sseUrl)

    sseSource = new EventSource(sseUrl)

    sseSource.onmessage = async (event) => {
      if (isDebug()) console.log('[OrayFastLoginSDK] SSE Message:', event.data)

      try {
        const msg = JSON.parse(event.data)
        if (msg.status === 'success') {
          // TODO: 确认状态返回值
          // 授权成功，关闭 SSE
          resetSubmission()
          isSubmitting = true // 重新标记，防止重复

          // 3. 换取 Token
          const tokenRes = await exchangeMobileToken(apiConfig, authCode)
          if (tokenRes && tokenRes.status === 200) {
            moduleConfig.onSuccess?.(tokenRes.data)
          } else {
            moduleConfig.onError?.(new Error('换取 Token 失败'))
          }
          isSubmitting = false
        } else if (msg.status === 'failed' || msg.status === 'cancel') {
          // TODO: 确认状态返回值
          resetSubmission()
          moduleConfig.onError?.(new Error('用户取消或授权失败'))
        }
      } catch (e) {
        // ignore
      }
    }

    sseSource.onerror = (e) => {
      console.error('[OrayFastLoginSDK] SSE Error:', e)
    }

    // 4. 唤起 APP
    const targetUrl = `${siteConfig.passport}/fast-login/auth?brand=${options.brand}&code=${authCode}`
    const deepLink = `${appScheme}://url=${encodeURIComponent(targetUrl)}`

    if (isDebug()) console.log('[OrayFastLoginSDK] Opening Deep Link:', deepLink)

    window.location.href = deepLink

    // 延时尝试 iframe 唤起
    const checkTimeout = 2500
    const start = Date.now()
    const timer = setTimeout(() => {
      const hidden = document.hidden || (document as any).webkitHidden
      if (!hidden && Date.now() - start < checkTimeout + 200) {
        if (downloadUrl) {
          if (isDebug()) console.log('[OrayFastLoginSDK] Launch failed, redirecting to download:', downloadUrl)
          window.location.href = downloadUrl
        } else {
          console.warn('[OrayFastLoginSDK] App launch failed and no downloadUrl provided.')
        }
      }
    }, checkTimeout)

    const visibilityHandler = () => {
      if (document.hidden || (document as any).webkitHidden) {
        clearTimeout(timer)
        document.removeEventListener('visibilitychange', visibilityHandler)
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)

    // iframe fallback
    setTimeout(() => {
      const iframe = document.createElement('iframe')
      iframe.style.display = 'none'
      iframe.src = deepLink
      document.body.appendChild(iframe)
      setTimeout(() => {
        document.body.removeChild(iframe)
      }, 1000)
    }, 2000)
  } catch (error: any) {
    resetSubmission()
    const err = new Error(error.message || '移动端快捷登录失败')
    moduleConfig.onError?.(err)
  }
}

/**
 * 开始轮询客户端授权状态
 */
function startPolling(portContext: PortProbeContext, account: Account) {
  if (pollTimer) clearTimeout(pollTimer)

  const executePoll = async () => {
    if (pollCountdown <= 0) {
      resetSubmission()
      moduleConfig.onError?.(new Error('授权超时，请重试'))
      return
    }

    pollCountdown--

    try {
      const env = moduleConfig.env || getGlobalEnv()
      const apiConfig = getApiConfig(env)
      const res = await localhostCheckLoginStateApi({
        baseUrl: apiConfig.localhostBase,
        port: portContext.port,
        timestamp: portContext.timestamp,
        signatureKey: portContext.signatureKey!,
        rd: portContext.rd,
      })

      if (isDebug()) {
        console.log('localhostCheckLoginStateApi', res)
      }

      if (res?.auth_status === 2 && res.code) {
        // 客户端已同意授权
        resetSubmission()
        await performFinalLogin(res.code)
      } else if (res?.auth_status === 3) {
        // 已取消授权或已过期
        resetSubmission()
        moduleConfig.onError?.(new Error('用户取消了授权'))
      } else {
        // 继续轮询
        pollTimer = setTimeout(executePoll, 2000)
      }
    } catch (e) {
      resetSubmission()
      moduleConfig.onError?.(new Error('轮询过程中发生错误'))
    }
  }

  pollTimer = setTimeout(executePoll, 2000)
}

/**
 * 调用 Web API 完成最终登录
 */
async function performFinalLogin(code: string) {
  const env = moduleConfig.env || getGlobalEnv()
  const apiConfig = getApiConfig(env)
  try {
    const result = await authorizeLoginApi(apiConfig.authApiBase, code)
    if (result && result.status === 200) {
      moduleConfig.onSuccess?.(result.data)
    } else {
      throw new Error('最终授权登录失败')
    }
  } catch (error: any) {
    moduleConfig.onError?.(new Error(error.message || '最终授权登录失败'))
  }
}

/**
 * 获取客户端状态
 */
async function getClientStatus(config: FastLoginConfig, apiConfig: ApiConfig): Promise<ClientStatus> {
  const ports = config.ports || []
  const SECRET_KEY = '3c15eb5a14f0576d5eb66d2ab0bd37f0'

  const promises = ports.map((port) => {
    const timestamp = Math.floor(Date.now() / 1000)
    const hmac = CryptoJS.HmacSHA256(String(timestamp), SECRET_KEY)
    const signature = CryptoJS.enc.Hex.stringify(hmac)
    const signaturePrefix = signature.slice(0, 16)
    const rd = generateRd()

    portProbeMap[port] = {
      port,
      timestamp,
      signaturePrefix,
      rd,
      signatureKey: null,
      clientName: null,
    }

    return localhostGenSignatureKeyApi({
      baseUrl: apiConfig.localhostBase,
      port,
      timestamp,
      signaturePrefix,
      rd,
    }).then((res) => ({ port, res }))
  })

  const signatureResults = await Promise.allSettled(promises)

  signatureResults.forEach((result) => {
    if (result.status === 'fulfilled' && result.value) {
      const { port, res } = result.value
      if (portProbeMap[port] && res) {
        portProbeMap[port].signatureKey = res.signature_key
      }
    }
  })

  const readyProbes = Object.values(portProbeMap).filter(
    (context): context is PortProbeContext & { signatureKey: string } => !!context.signatureKey,
  )

  if (readyProbes.length > 0) {
    const loginStatePromises = readyProbes.map((context) => {
      return localhostCheckLoginStateApi({
        baseUrl: apiConfig.localhostBase,
        port: context.port,
        timestamp: context.timestamp,
        signatureKey: context.signatureKey,
        rd: context.rd,
      }).then((res) => ({ port: context.port, res }))
    })

    const loginStateResults = await Promise.allSettled(loginStatePromises)
    const accountList: Account[] = []
    loginStateResults.forEach((result) => {
      if (result.status === 'fulfilled' && result.value.res?.auth_status === 0) {
        const { port, res } = result.value
        if (portProbeMap[port]) {
          accountList.push({
            userid: res.userid as number,
            account: res.account as string,
            avatar: res.avatar as string,
            source: res.client_name,
          })
          portProbeMap[port].clientName = res.client_name
        }
      }
    })
    return { isLoggedIn: true, accounts: accountList }
  }

  return { isLoggedIn: false, accounts: [] }
}

async function getWebStatus(config: FastLoginConfig, apiConfig: ApiConfig): Promise<WebStatus> {
  if (config.userInfo) {
    return {
      isLoggedIn: true,
      currentUser: {
        userid: config.userInfo.userid,
        account: config.userInfo.account,
        avatar: config.userInfo.avatar,
        source: 'web',
      },
    }
  }
  const result = await fetchWebStatus(apiConfig)
  if (!result) return { isLoggedIn: false }
  if (result.status === 401) return { isLoggedIn: false }
  if (result.status === 200 && result.data) {
    const { data } = result
    return {
      isLoggedIn: true,
      currentUser: {
        userid: typeof data.userid === 'number' ? data.userid : parseInt(data.userid, 10),
        account: data.account || 'Unknown',
        avatar: data.avatar || undefined,
        source: 'web',
      },
    }
  }
  return { isLoggedIn: false }
}

function handleStrategy(currentConfig: FastLoginConfig, result: DecisionResult) {
  if (location.hostname === 'passport.oray.com' || location.hostname === 'passport.oraybeta.com') return
  const env = currentConfig.env || getGlobalEnv()

  if (isDebug()) {
    console.log('[OrayFastLoginSDK] Handling strategy:', result.strategy, result.data)
  }
  if ([LoginStrategy.SHOW_SPECIFIED_LOGIN, LoginStrategy.SHOW_CONFLICT_LOGIN].includes(result.strategy) && result.data?.clientAccount) {
    const site = getSiteConfig(env)
    window.location.href = `${site.passport}/login?s_url=${encodeURIComponent(window.location.href)}`
  }
}

/**
 * SDK 初始化入口
 * oray.fastLogin.init
 */
export async function init(config: FastLoginConfig = {}) {
  if (!isValidOrigin(window.location.origin)) {
    return null
  }

  if (initialized && lastDecision) {
    if (isDebug()) {
      console.log('[OrayFastLoginSDK] Already initialized, returning last decision.')
    }
    return lastDecision
  }

  moduleConfig = { ...moduleConfig, ...config }

  if (!moduleConfig.env) {
    moduleConfig.env = getGlobalEnv()
  }

  if (moduleConfig.debug === undefined && isDebug()) {
    moduleConfig.debug = true
  }

  const apiConfig = getApiConfig(moduleConfig.env)

  if (moduleConfig.onlyCheckWithClParam) {
    const clParam = parseClientParam(window.location.search)
    if (!clParam) {
      if (isDebug()) {
        console.log('[OrayFastLoginSDK] No _cl param found, skipping detection.')
      }
      const decision: DecisionResult = { strategy: LoginStrategy.NORMAL_LOGIN }
      if (moduleConfig.autoHandleStrategy) {
        handleStrategy(moduleConfig, decision)
      }
      return decision
    }
  }

  try {
    if (isDebug()) {
      console.log(`[OrayFastLoginSDK] Initializing in ${moduleConfig.env} mode...`)
    }

    const isMobileDevice = isMobile()
    let decision: DecisionResult

    if (isMobileDevice) {
      if (isDebug()) {
        console.log('[OrayFastLoginSDK] Running on Mobile mode.')
      }
      const webStatus = await getWebStatus(moduleConfig, apiConfig)
      const options: FastLoginOptions = {}
      decision = decideMobileLoginStrategy(webStatus, options)
    } else {
      if (isDebug()) {
        console.log('[OrayFastLoginSDK] Running on PC mode.')
      }
      const [clientStatus, webStatus] = await Promise.all([getClientStatus(moduleConfig, apiConfig), getWebStatus(moduleConfig, apiConfig)])
      const options: FastLoginOptions = {}
      decision = decidePCLoginStrategy(clientStatus, webStatus, options)
    }

    if (isDebug()) {
      console.log('[OrayFastLoginSDK] Decision result:', decision)
    }

    if (moduleConfig.autoHandleStrategy) {
      handleStrategy(moduleConfig, decision)
    }

    initialized = true
    lastDecision = decision

    return decision
  } catch (error) {
    console.error('[OrayFastLoginSDK] Initialization failed:', error)
    const fallbackDecision: DecisionResult = { strategy: LoginStrategy.NORMAL_LOGIN }
    if (moduleConfig.autoHandleStrategy) {
      handleStrategy(moduleConfig, fallbackDecision)
    }
    return fallbackDecision
  }
}
